import React, { memo } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { styled } from '@mui/material/styles';
import { OrgChartHrMapping } from 'routes/HumanResourcesModule/Settings/OrgChartHrMappings/helpers/type';
import { useOrgChartHrMappingsContext } from 'routes/HumanResourcesModule/Settings/OrgChartHrMappings/components/OrgChartHrMappingContext';

type MappingOptionType = {
    inputValue?: string;
    title: string;
};

const filterMappingOptions = createFilterOptions<MappingOptionType>();

const StyAutoComplete = styled(Autocomplete, {
    name: 'StyTextFieldCellRendered',
})(() => ({
    display: 'flex',
    width: 300,
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiFormControl-root': {
        padding: 0,
        margin: '6px',
        '& .MuiInputBase-root': {
            paddingBottom: 0,
            paddingTop: 0,
            height: '100%',
        },
        '& .MuiInputBase-input': {
            fontSize: '11px',
            height: 'inherit',
        },
    },
}));

type Props = {
    value?: { title: string } | null;
    handleChange: (
        event: React.SyntheticEvent,
        newValue: OrgChartHrMapping | MappingOptionType | null,
    ) => void;
};

function MappingChoices({ value = null, handleChange }: Props) {
    const orgChartHrMappings = useOrgChartHrMappingsContext();
    return (
        <StyAutoComplete
            value={value?.title || null}
            onChange={handleChange}
            filterOptions={(options: MappingOptionType[], params) => {
                const filtered = filterMappingOptions(options, params);

                const { inputValue } = params;
                // Suggest the creation of a new value
                const isExisting = options.some(
                    (option) => inputValue === option.title,
                );
                if (inputValue !== '' && !isExisting) {
                    filtered.push({
                        inputValue,
                        title: `Ajouter "${inputValue}"`,
                    });
                }

                return filtered;
            }}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            options={orgChartHrMappings}
            getOptionLabel={(option: MappingOptionType) => {
                // Value selected with enter, right from the input
                if (typeof option === 'string') {
                    return option;
                }
                // Add "xxx" option created dynamically
                if (option.inputValue) {
                    return option.inputValue;
                }
                // Regular option
                return option.title;
            }}
            renderOption={(props, option: MappingOptionType) => (
                <li {...props}>{option.title}</li>
            )}
            freeSolo
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    margin="dense"
                    size="small"
                    InputProps={{
                        ...params.InputProps,
                        disableInjectingGlobalStyles: true,
                    }}
                />
            )}
        />
    );
}

export default memo(MappingChoices);
